import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface FormData {
  name: string;
  email: string;
  type: string;
  country: string;
  message: string;
}

const INITIAL_FORM: FormData = {
  name: '',
  email: '',
  type: '',
  country: '',
  message: '',
};

export default function Contact() {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Limpiar el estado de éxito/error al escribir en el formulario
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!privacyAccepted) {
      setSubmitStatus('error');
      setErrorMessage('Debes aceptar la Política de Privacidad para continuar.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Construir el campo empresa: NUNCA undefined, siempre string o null
      let empresaValue: string | null = null;
      
      if (formData.type === 'empresa') {
        empresaValue = formData.country.trim() || null;
      } else if (formData.type === 'profesional') {
        empresaValue = formData.country.trim() 
          ? `Profesional · ${formData.country.trim()}` 
          : 'Profesional';
      } else {
        empresaValue = formData.country.trim() || null;
      }

      // Insertar en Supabase
      const { error: supabaseError } = await supabase
        .from('contact_form')
        .insert({
          nombre: formData.name.trim(),
          email: formData.email.trim(),
          empresa: empresaValue,
          mensaje: formData.message.trim(),
        });

      if (supabaseError) {
        // Manejo específico de error 403 (RLS)
        if (supabaseError.code === '42501' || supabaseError.message.includes('policy')) {
          throw new Error('Error de permisos: La política de seguridad no permite esta operación. Contacta al administrador.');
        }
        // Mostrar el error real de Supabase
        throw new Error(supabaseError.message || JSON.stringify(supabaseError));
      }

      // Éxito en Supabase
      setSubmitStatus('success');
      setFormData(INITIAL_FORM);
      setPrivacyAccepted(false);

      // Opcional: También enviar al sistema de formularios de Readdy (no bloquea el éxito)
      try {
        const formBody = new URLSearchParams();
        formBody.append('name', formData.name);
        formBody.append('email', formData.email);
        formBody.append('type', formData.type);
        formBody.append('country', formData.country);
        formBody.append('message', formData.message);

        await fetch('https://readdy.ai/api/form/d6gr88ptp4hj31tchqog', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        });
      } catch (readdyError) {
        // Silencioso: no afecta el éxito del envío principal
        console.warn('Envío opcional a Readdy falló:', readdyError);
      }

    } catch (error: unknown) {
      console.error('Error al enviar formulario:', error);
      const msg =
        error instanceof Error
          ? error.message
          : 'Error desconocido al enviar el formulario.';
      setErrorMessage(msg);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-[#F8F6F3]">
      <div className="max-w-4xl mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-[#1A2B4A] mb-4">Hablemos</h2>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <i className="ri-lock-line text-lg"></i>
            <span className="text-sm font-medium">Confidencialidad garantizada</span>
          </div>
        </div>

        <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <input
              type="text"
              name="name"
              placeholder="Nombre completo"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent transition-all"
            />
          </div>

          <div>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent transition-all"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <select
                name="type"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                required
                className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent transition-all cursor-pointer"
              >
                <option value="">Selecciona una opción</option>
                <option value="empresa">Soy empresa</option>
                <option value="profesional">Soy profesional</option>
              </select>
            </div>

            <div>
              <input
                type="text"
                name="country"
                placeholder="País"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                required
                className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div>
            <textarea
              name="message"
              placeholder="Cuéntanos sobre tu necesidad o perfil profesional"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              required
              maxLength={500}
              rows={5}
              className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent transition-all resize-none"
            ></textarea>
            <p className="text-xs text-gray-500 mt-2">
              {formData.message.length}/500 caracteres
            </p>
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="privacy-checkbox"
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              required
              className="mt-1 w-4 h-4 text-[#1A2B4A] border-gray-300 rounded focus:ring-[#1A2B4A] cursor-pointer"
            />
            <label htmlFor="privacy-checkbox" className="text-sm text-gray-600 cursor-pointer">
              He leído y acepto la{' '}
              <a href="/politica-privacidad" target="_blank" className="text-[#1A2B4A] hover:underline">
                Política de Privacidad
              </a>.
            </label>
          </div>

          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting || !privacyAccepted}
              className="inline-flex items-center justify-center px-12 py-4 bg-[#1A2B4A] text-white text-base font-medium rounded-lg hover:bg-[#2A3B5A] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap group"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2 text-lg"></i>
                  Enviando...
                </>
              ) : (
                <>
                  Enviar solicitud
                  <i className="ri-arrow-right-line ml-2 text-lg group-hover:translate-x-1 transition-transform"></i>
                </>
              )}
            </button>
          </div>

          {submitStatus === 'success' && (
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
              </div>
              <p className="text-green-800 font-medium">
                Enviado correctamente. Nos pondremos en contacto contigo pronto.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-red-600 text-xl"></i>
              </div>
              <p className="text-red-800 font-medium">{errorMessage}</p>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}